/* BRIEF 131 §2 — ONE MATTER PICKER, EXTRACTED FROM TWO.
 *
 * This is `TimeEntryModal.tsx:455-617` and `AgendaEventModal.tsx:596-789`,
 * which were one component written twice — the copy left its fingerprint in
 * the i18n keys, the event modal's placeholder still being `modal.te.…`.
 *
 * ── 150 §3 — THE SHAPE MOVED; THE MATTER HALF STAYED ───────────────────────
 * The type-ahead machinery — input, completion, anchored portal menu, the
 * recents/search arms, the five/seven caps, the "+N more" row — now lives in
 * `TypeaheadPicker.tsx`, because the user filter needs the same shape pointed
 * at collaborators and the brief forbids a second type-ahead. This file is the
 * wrapper that owns everything MATTER-specific, and its props are unchanged —
 * the five 131 hosts see nothing:
 *
 *   - the ca/cc merge with `_type`, and the `excludeArchived` /
 *     `restrictToAssigned` / `sortByTitle` filters (131 §2's, in their order);
 *   - the recents resolution: `getRecentCases` hands bare ids, and an id is
 *     found in the MERGED base — cases first — exactly as before;
 *   - the selected label: the id may name either kind (131 §6 measured the
 *     id-collision), so both lists are consulted, cases first, unless the
 *     caller states `selectedKind` (132 §1);
 *   - `query`/`setQuery` stay LIFTED for the two modal hosts (125 §3 — five
 *     sites outside the picker write it); the core owns the state only when
 *     the caller omits it, which is the default for a new mount.
 *
 * The option mapping is title → label, ref → mono, so the search predicate is
 * still exactly `title + ref` (134 §1(c): a lawyer types `2026/001` as readily
 * as a name) and the completion still completes the TITLE. The 133/134 history
 * of each mechanism now lives at the mechanism, in TypeaheadPicker.tsx.
 */
import { useMemo } from "react";
import type { CSSProperties } from "react";
import type { TFn } from "../lib/taxonomyTypes";
import TypeaheadPicker from "./TypeaheadPicker";
import type { TypeaheadOption } from "./TypeaheadPicker";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`
import { SK } from "../utils"; // 210 §1 — the two lists a matter is picked from

/* 210 §1 — a matter is picked from the cases AND the collective cases: while
   either could not load, every host's picker says so (TypeaheadPicker's
   `lists`). What prod showed was a picker offering one collective case and no
   case. */
const MATTER_LISTS: readonly string[] = [SK.ca, SK.cc];

export type MatterKind = "ca" | "cc";

/** 184 A.1 — a matter id as a modal form holds it (a number, a numeric string,
 *  `""` for a cleared picker, null/undefined) → the id, or null for none. The
 *  two modals write `""` for the other kind on a pick; reading it as "none"
 *  here is what lets `??` choose between the two ids. */
export function formMatterId(v: unknown): number | string | null {
  if (v === null || v === undefined || v === "") return null;
  return typeof v === "number" || typeof v === "string" ? v : null;
}
/** 184 A.1 — which list a form's matter came from (`selectedKind`, 132 §1):
 *  the collective id wins when present, as every route's gate reads it. */
export function formMatterKind(caId: unknown, ccId: unknown): MatterKind | null {
  if (formMatterId(ccId) != null) return "cc";
  if (formMatterId(caId) != null) return "ca";
  return null;
}

/** What the picker actually reads off a matter — structural, so a `Case` and a
 *  `CollectiveCase` both satisfy it. `55 §3` named the collective case's
 *  `status` exactly as `Case` names it, which is why one filter serves both. */
export type MatterOption = {
  id: number;
  title?: string;
  ref?: string;
  status?: string;
  caseCollaborators?: { coId?: unknown }[];
};

/** A row as picked: the matter, plus which list it came out of. */
export type PickedMatter = MatterOption & { _type: MatterKind };

export type MatterPickerProps = {
  t: TFn;
  cases: MatterOption[];
  collectiveCases: MatterOption[];
  /** The id currently written (`form.caId`, `form.caseId`, …), of either kind. */
  selectedId?: number | string | null;
  /** WHICH LIST `selectedId` came out of, when the caller knows (132 §1) —
   *  Case and CollectiveCase ids come from different sequences, so id 7 can
   *  name one of each, and the label lookup resolves cases first otherwise. */
  selectedKind?: MatterKind | null;
  /** `null` = menu closed; `""` = open on recents; anything else = search.
   *  Optional — omitting it is the DEFAULT for a new mount (the picker owns
   *  the state; renderModals unmounting resets it structurally). The two
   *  modal hosts pass it because five sites outside the picker write it. */
  query?: string | null;
  setQuery?: (v: string | null) => void;
  /** Resolved through `t()` AT THE CALL SITE, so each screen keeps its own key
   *  as a literal and the i18n census can still see it. */
  placeholder: string;
  onPick: (matter: PickedMatter) => void;
  /** Fired when the field is emptied by typing, and by the clear row. */
  onClear: () => void;
  /** Widened to what both modals already declare — the ids come back off
   *  `JSON.parse`, so the array is `readonly unknown[]` at the boundary and
   *  the `===` below stays the original's `===`. */
  getRecentCases: (userId: unknown) => readonly unknown[];
  currentUser?: unknown;
  restrictToAssigned?: boolean;
  excludeArchived?: boolean;
  sortByTitle?: boolean;
  /** Resolved string. Present ⇒ the clear row renders above the list. */
  clearLabel?: string;
  /** 150 §3 — forwarded to the core so a FILTER host can dress the input for
   *  its `.search-box` shell; the modal hosts omit them and keep the
   *  field-input dress the five 131 sites have always had. */
  inputClassName?: string;
  inputStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
};

type MatterRow = PickedMatter & TypeaheadOption;

export default function MatterPicker({
  t,
  cases,
  collectiveCases,
  selectedId,
  selectedKind,
  query,
  setQuery,
  placeholder,
  onPick,
  onClear,
  getRecentCases,
  currentUser,
  restrictToAssigned = false,
  excludeArchived = false,
  sortByTitle = false,
  clearLabel,
  inputClassName,
  inputStyle,
  wrapperStyle,
}: MatterPickerProps) {
  /* THE FILTERED POPULATION. The filters, their order and their props are
     131 §2's, unchanged; the mapping to the core's option shape is the only
     addition (title → label, ref → mono — the search predicate stays
     `title + ref`). */
  const options = useMemo<MatterRow[]>(() => {
    let out: PickedMatter[] = [
      ...cases.map((c) => ({ ...c, _type: "ca" as const })),
      ...collectiveCases.map((c) => ({ ...c, _type: "cc" as const })),
    ];
    if (excludeArchived) out = out.filter((c) => c.status !== "archived");
    if (restrictToAssigned && currentUser)
      out = out.filter((c) =>
        (c.caseCollaborators || []).some((x) => x.coId === currentUser),
      );
    if (sortByTitle)
      out = out.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    return out.map((c) => ({
      ...c,
      key: c._type + c.id,
      label: c.title || "",
      mono: c.ref,
    }));
  }, [cases, collectiveCases, excludeArchived, restrictToAssigned, currentUser, sortByTitle]);

  /* The recents arm: bare ids out of `lx:recentCases:<userId>`, resolved in
     the MERGED base — cases first, the order they have always had. Called
     inside the core's model memo, exactly where the original called it. */
  const getRecents = useMemo(
    () => () =>
      getRecentCases(currentUser)
        .map((id) => options.find((c) => c.id === id))
        .filter((c): c is MatterRow => Boolean(c)),
    [getRecentCases, currentUser, options],
  );

  /* The label shown when the menu is closed and a matter is chosen: the id may
     name either kind, so both lists are consulted, cases first — unless the
     caller states the kind, in which case only that list is (132 §1). */
  const selectedLabel = (() => {
    if (!selectedId) return "";
    const _c = selectedKind === "cc" ? undefined : cases.find((c) => c.id === +selectedId);
    const _cc =
      selectedKind === "ca" ? undefined : collectiveCases.find((c) => c.id === +selectedId);
    const _m = _c || _cc;
    return _m ? _m.title + " — " + _m.ref : "";
  })();

  return (
    <TypeaheadPicker<MatterRow>
      t={t}
      options={options}
      selectedLabel={selectedLabel}
      query={query}
      setQuery={setQuery}
      placeholder={placeholder}
      onPick={onPick}
      onClear={onClear}
      getRecents={getRecents}
      clearLabel={clearLabel}
      moreLabel={(n) =>
        t("picker.more_matters", withCount(n, "n", { fallback: "+{n} more — type to narrow" }))
      }
      inputClassName={inputClassName}
      inputStyle={inputStyle}
      wrapperStyle={wrapperStyle}
      lists={MATTER_LISTS}
    />
  );
}
